import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatPlacement } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { Empty } from '@/components/ui/Empty';
import { Badge } from '@/components/ui/Badge';

export const Route = createFileRoute('/dancer/competitions')({
  component: DancerCompetitions,
});

// Standalone dancer competitions view. RLS lets a 13+ dancer with consent
// see their own row in `dancers` (Sprint 0). cast_assignments → dances →
// competition_routines join is bounded by RLS at every hop.

function useDancerCompetitions() {
  return useQuery({
    queryKey: ['dancer-competitions'],
    queryFn: async () => {
      const user = await supabase.auth.getUser();
      const userId = user.data.user?.id;
      if (!userId) return [];
      // Resolve the dancer record for the logged-in user.
      const { data: dancerRow } = await supabase
        .from('dancers')
        .select('id, first_name, last_name')
        .eq('auth_user_id', userId)
        .maybeSingle();
      if (!dancerRow) return [];
      const dancerId = (dancerRow as { id: string }).id;

      const { data, error } = await supabase
        .from('cast_assignments')
        .select(
          `id,
           dances!inner(name,
             competition_routines!inner(id, scheduled_at,
               competitions(name, start_date, end_date),
               routine_placements(placement, total_competitors, score, award)))`,
        )
        .eq('dancer_id', dancerId);
      if (error) throw error;
      return data ?? [];
    },
  });
}

function DancerCompetitions() {
  const entries = useDancerCompetitions();

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Your competition weekends
        </p>
        <h1 className="mt-2">Competitions</h1>
      </header>

      {entries.isLoading && <p aria-live="polite">Loading…</p>}

      {entries.data?.length === 0 && (
        <Empty
          title="No competition entries yet"
          description="When the studio enters you in a competition, it'll appear here."
        />
      )}

      {entries.data && entries.data.length > 0 && (
        <ul className="grid gap-3">
          {(
            entries.data as {
              id: string;
              dances: {
                name: string;
                competition_routines: {
                  id: string;
                  scheduled_at: string | null;
                  competitions: { name: string; start_date: string; end_date: string } | null;
                  routine_placements: {
                    placement: number | null;
                    total_competitors: number | null;
                    score: number | null;
                    award: string | null;
                  }[];
                }[];
              } | null;
            }[]
          ).map((r) => {
            const dance = r.dances;
            if (!dance) return null;
            return dance.competition_routines.map((cr) => {
              const comp = cr.competitions;
              if (!comp) return null;
              const placement = cr.routine_placements[0];
              return (
                <li key={`${r.id}-${cr.id}`} className="surface-elevated p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div>
                      <h3 className="font-medium">{dance.name}</h3>
                      <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                        {comp.name} · {comp.start_date}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-baseline gap-1">
                      {placement?.placement !== undefined && placement?.placement !== null && (
                        <Badge tone={placement.placement <= 3 ? 'success' : 'neutral'}>
                          {formatPlacement(placement.placement, placement.total_competitors)}
                        </Badge>
                      )}
                      {placement?.score !== null && placement?.score !== undefined && (
                        <Badge tone="info">{placement.score.toFixed(2)}</Badge>
                      )}
                    </div>
                  </div>
                </li>
              );
            });
          })}
        </ul>
      )}
    </div>
  );
}
